/**
 * Browsers block audio playback until the page is allowed to make sound — usually
 * after the first user gesture, sometimes right away depending on the autoplay
 * policy. This module tracks that permission once for the whole viewer.
 *
 * Permission is kept as queryable state instead of a single event, because a
 * gesture that happened before this module ran would otherwise stay invisible.
 */

export const AUDIO_PLAYBACK_PERMITTED_EVENT = 'spotar:audioPlaybackPermitted';

const GESTURE_EVENT_NAMES = ['pointerdown', 'touchstart', 'keydown', 'click'] as const;

let isAudioPlaybackPermitted = false;
let autoplayProbe: AudioContext | null = null;
let resolvePermission!: () => void;

const audioPlaybackPermission = new Promise<void>((resolve) => {
  resolvePermission = resolve;
});

export function hasAudioPlaybackPermission(): boolean {
  return isAudioPlaybackPermitted;
}

/** Resolves immediately once playback is permitted, otherwise as soon as it is. */
export function waitForAudioPlaybackPermission(): Promise<void> {
  return audioPlaybackPermission;
}

export function onAudioPlaybackPermitted(listener: () => void): () => void {
  window.addEventListener(AUDIO_PLAYBACK_PERMITTED_EVENT, listener);
  return () => window.removeEventListener(AUDIO_PLAYBACK_PERMITTED_EVENT, listener);
}

function permitAudioPlayback() {
  if (isAudioPlaybackPermitted) return;
  isAudioPlaybackPermitted = true;
  stopListeningForUserGesture();
  closeAutoplayProbe();
  resolvePermission();
  window.dispatchEvent(new Event(AUDIO_PLAYBACK_PERMITTED_EVENT));
}

function listenForUserGesture() {
  for (const eventName of GESTURE_EVENT_NAMES) {
    window.addEventListener(eventName, permitAudioPlayback, { capture: true, once: true });
  }
}

function stopListeningForUserGesture() {
  for (const eventName of GESTURE_EVENT_NAMES) {
    window.removeEventListener(eventName, permitAudioPlayback, true);
  }
}

/** Covers gestures made before this module loaded, e.g. on a lazily imported viewer. */
function hasEarlierUserActivation(): boolean {
  return navigator.userActivation?.hasBeenActive === true;
}

function readAudioContextConstructor(): typeof AudioContext | undefined {
  const legacyWindow = window as unknown as { webkitAudioContext?: typeof AudioContext };
  return window.AudioContext ?? legacyWindow.webkitAudioContext;
}

function closeAutoplayProbe() {
  const probe = autoplayProbe;
  autoplayProbe = null;
  probe?.close().catch(() => {});
}

/**
 * A fresh AudioContext starts as `running` when the autoplay policy already
 * allows sound and as `suspended` while it does not. This covers pages that may
 * play audio without any interaction, where no gesture would ever arrive.
 * Calling `resume()` is deliberately avoided: it only logs a browser warning
 * while playback is blocked, and the gesture listeners already cover that case.
 */
function watchAutoplayPolicy() {
  const AudioContextConstructor = readAudioContextConstructor();
  if (!AudioContextConstructor) return;

  const probe = new AudioContextConstructor();
  autoplayProbe = probe;

  const permitWhenRunning = () => {
    if (probe.state !== 'running') return;
    permitAudioPlayback();
  };

  probe.addEventListener('statechange', permitWhenRunning);
  permitWhenRunning();
}

function detectAudioPlaybackPermission() {
  if (typeof window === 'undefined') return;
  listenForUserGesture();
  watchAutoplayPolicy();
  if (hasEarlierUserActivation()) permitAudioPlayback();
}

detectAudioPlaybackPermission();
